import {
  APP_PATHS,
  appPath,
  MY_TIME_OFF_TABS,
  SERVICE_REQUEST_TYPES,
  TEAM_TIME_OFF_TABS,
} from '@/constant/app-paths';
import { linkToRecord } from '@/hooks/use-linked-record';

/** A page's tab, opened on one record when there is one (`linkToRecord` alone drops the tab without). */
const tabLink = (path: string, tab: string, itemId?: string) =>
  itemId ? linkToRecord(path, itemId, tab) : `${path}?tab=${tab}`;
import type { Notification } from '../types/notification.types';

/** What a notification says, from its `denormalizedPayload` (a JSON text); empty when it is not one. */
export interface NotificationContent {
  title: string;
  description: string;
  /** The record it is about (blocks/workflows lib/notices.js notifyBody); '' for older notices and Blocks' own. */
  entityId: string;
}

export const notificationContent = (
  denormalizedPayload: string | undefined
): NotificationContent => {
  let parsed: Record<string, unknown> = {};
  try {
    const value: unknown = JSON.parse(denormalizedPayload ?? '');
    if (value && typeof value === 'object') parsed = value as Record<string, unknown>;
  } catch {
    // Not ours: shown without text rather than breaking the whole list.
  }
  const text = (key: string) => (typeof parsed[key] === 'string' ? (parsed[key] as string) : '');
  // Blocks' own notices (e.g. "Key Translation Completed") write their text in lower case.
  return {
    title: text('Title') || text('title'),
    description: text('Description') || text('description'),
    entityId: text('EntityId'),
  };
};

/**
 * The page a notification opens, from the `responseKey` / `responseValue` the workflows send
 * (blocks/workflows: lib/notices.js notificationRequest and each module's notices). The value says who
 * was told: a request handed to its reviewers opens the manager page, a decision on the person's own
 * request opens their own page. With the record's id (`entityId`) the page opens that record
 * (useLinkedRecord); a break or an escalation names the employee, whose attendance opens. `null` for
 * anything else (e.g. Blocks' own notices): nothing to open.
 */
export const notificationTarget = (
  payload: Pick<Notification['payload'], 'responseKey' | 'responseValue'> | undefined,
  entityId = ''
): string | null => {
  const key = payload?.responseKey ?? '';
  const value = payload?.responseValue ?? '';
  const employeeAttendance = entityId
    ? appPath.teamAttendanceOf(entityId)
    : APP_PATHS.TEAM_ATTENDANCE;
  switch (key) {
    case 'LeaveRequest':
      // Submitted / withdrawn go to the department heads; reviewed / cancelled to the employee.
      return value === 'LeaveRequestSubmitted' || value === 'LeaveRequestWithdrawn'
        ? tabLink(APP_PATHS.TEAM_TIME_OFF, TEAM_TIME_OFF_TABS.REQUESTS, entityId)
        : tabLink(APP_PATHS.MY_TIME_OFF, MY_TIME_OFF_TABS.HISTORY, entityId);
    case 'ToilRequest':
      // TOIL Requests are tabs of the Time Off pages (the TOIL pages until Phase 2).
      return value === 'Requested'
        ? tabLink(APP_PATHS.TEAM_TIME_OFF, TEAM_TIME_OFF_TABS.TOIL, entityId)
        : tabLink(APP_PATHS.MY_TIME_OFF, MY_TIME_OFF_TABS.TOIL, entityId);
    case 'AttendanceCorrection':
      return linkToRecord(
        value === 'Requested' ? APP_PATHS.TEAM_ATTENDANCE : APP_PATHS.ATTENDANCE,
        entityId,
        'corrections'
      );
    case 'AttendanceBreak':
      return employeeAttendance;
    case 'AttendanceReminder':
      return value === 'Escalation' ? employeeAttendance : APP_PATHS.ATTENDANCE;
    default:
      // The page's first tab lists the application: a reviewer's requests (their own included), else
      // the person's own.
      return SERVICE_DESK_PATHS[key] ? linkToRecord(SERVICE_DESK_PATHS[key], entityId) : null;
  }
};

/** Service desk notices name their application schema; one page per application type. */
const SERVICE_DESK_PATHS: Record<string, string> = {
  NocApplication: appPath.serviceRequests(SERVICE_REQUEST_TYPES.NOC),
  ByodApplication: appPath.serviceRequests(SERVICE_REQUEST_TYPES.BYOD),
  BusinessCardApplication: appPath.serviceRequests(SERVICE_REQUEST_TYPES.BUSINESS_CARD),
  IdCardApplication: appPath.serviceRequests(SERVICE_REQUEST_TYPES.ID_CARD),
  MetLifeApplication: appPath.serviceRequests(SERVICE_REQUEST_TYPES.INSURANCE),
};
